import React, { useContext, useEffect, useState } from "react";
import {
  Table,
  Pagination,
  Popup,
  Icon,
  Checkbox,
  Dropdown,
  Button,
} from "semantic-ui-react";
import { observer } from "mobx-react-lite";
import { RootStoreContext } from "../../app/stores/rootStore";
import { Link } from "react-router-dom";
import { pageSizeOptions } from "../../app/util/listOfValoursOptions";
import LoadingComponent from "../../app/layout/LoadingComponent";
import ResendMail from "./ResendMail";
import ResendBulkMailForm from "./ResendBulkMailForm";

interface IProps {
  match: string;
}

interface ISortableHeaderCellProps {
  columnKey: string;
  children: React.ReactNode;
  onSort: (columnName: string) => void;
  getSortDirection: (columnName: string) => 'ascending' | 'descending' | null;
  currentSortBy?: string;
  currentSortDirection?: 'asc' | 'desc';
}

const SortableHeaderCell: React.FC<ISortableHeaderCellProps> = ({
  columnKey,
  children,
  onSort,
  getSortDirection,
  currentSortBy,
  currentSortDirection,
}) => (
  <Table.HeaderCell 
    style={{ cursor: 'pointer' }}
    onClick={() => onSort(columnKey)}
    sorted={getSortDirection(columnKey)}
  >
    {children}
    {currentSortBy === columnKey && (
      <Icon 
        name={currentSortDirection === 'asc' ? 'caret up' : 'caret down'} 
        style={{ marginLeft: '5px' }}
      />
    )}
  </Table.HeaderCell>
);

const getAvialableBulkMailIds = (documents) =>
  documents.documentList.map((document) => ({
    id: document.id,
    hasGraphicFlag: document.hasGraphicFlag,
  })).filter((document) => document.hasGraphicFlag)

const getBulkMailIds = (selectedBulkMail: Record<string, boolean>):string[] =>
  Object.entries(selectedBulkMail).filter(([key, value]) => value === true).map(([key, value]) => key);

const ListTrackDocuments: React.FC<IProps> = ({ match }) => {
  //variables para uso la informacion del userStore
  const rootStore = useContext(RootStoreContext);
  const {
    btFilter,
    changePage,
    changePageSize,
    page,
    pageSize,
    documents,
    getTrackDocuments,
    loadData,
    setSorting,
    filter,
  } = rootStore.trackDocumentStore;
  const { openModal } = rootStore.modalStore;

  const [selectedBulkMail, setSelectedBulkMail] = useState({});

  useEffect(() => {
    getTrackDocuments();
  }, [getTrackDocuments]);

  useEffect(() => {
    if (btFilter) {
      setSelectedBulkMail({});
    }
  }, [btFilter]);

  if (!loadData) return <LoadingComponent content="Cargando" />;

  const availableBulkMailIds = getAvialableBulkMailIds(documents);

  const bulkMailIds = getBulkMailIds(selectedBulkMail);
  const hasSelectedMails = bulkMailIds.length > 0;


  const handleClearSelectedBulkMail = () => {
    setSelectedBulkMail({});
  };

  const handleSelectAllAvailableBulkMail = () => {
    availableBulkMailIds.forEach((document) => {
      setSelectedBulkMail((current) => ({ ...current, [document.id]: true }));
    });
  };

  const handlePageSizeChange = (e: any, { value }: any) => {
    if (value < pageSize) {
      handleClearSelectedBulkMail();
    }

    changePageSize(value);
  };

  const handlePaginationChange = (e: any, { activePage }: any) => {
    changePage(activePage);
    handleClearSelectedBulkMail();
  };

  const handleSelectedBulkMail = (e: any) => {
    setSelectedBulkMail((current) => ({ ...current, [e.target.name]: e.target.checked }));
  };

  const handleSelectedAllBulkMail = () => {
    if (availableBulkMailIds.length <= bulkMailIds.length) {
      handleClearSelectedBulkMail();
      return;
    }

    handleSelectAllAvailableBulkMail();
  };

  const handleColumnSort = (columnName: string) => {
    setSorting(columnName);
  };

  const getSortDirection = (columnName: string) => {
    if (filter.sortBy === columnName) {
      return filter.sortDirection === 'asc' ? 'ascending' : 'descending';
    }
    return null;
  };


  return (
    <div>
      <div className={`bulk-mail-actions`}>
        <Button
          disabled={!hasSelectedMails}
          className="ui primary button"
          onClick={() =>
            openModal(
              <ResendBulkMailForm documentsId={bulkMailIds} clearSelected={handleClearSelectedBulkMail} />,
              "Reenviar mails"
            )
          }
          color="blue"
          type="submit"
          content="Reenviar mail"
        />
      </div>
      <div className="TableScroll">
        <Table striped basic unstackable compact singleLine>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                <Checkbox
                  type="checkbox"
                  name="all-mails"
                  className="all-mails-selector"
                  onChange={handleSelectedAllBulkMail}
                  disabled={availableBulkMailIds.length === 0}
                  checked={availableBulkMailIds.length > 0 && availableBulkMailIds.length <= bulkMailIds.length}
                  indeterminate={bulkMailIds.length > 0 && availableBulkMailIds.length > bulkMailIds.length}
                />
              </Table.HeaderCell>
              <Table.HeaderCell>Acciones</Table.HeaderCell>
              <SortableHeaderCell 
                columnKey="documentType"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                Tipo comprobante
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="erpDocumentId"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                No. Comprobante ERP
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="docId"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                No. Comprobante DIAN
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="documentValue"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                Valor
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="issueDate"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                Fecha comprobante
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="receptionDate"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                Fecha recepción
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="acquirerIdentification"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                Identificación Cliente
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="acquirerName"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                Nombre cliente
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="validationDate"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                Fecha Envío Dian
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="processStateMessage"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                Estado comprobante
              </SortableHeaderCell>
              <SortableHeaderCell 
                columnKey="emailStateAcquirer"
                onSort={handleColumnSort}
                getSortDirection={getSortDirection}
                currentSortBy={filter.sortBy}
                currentSortDirection={filter.sortDirection}
              >
                Estado mail adquiriente
              </SortableHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {documents?.length > 0 ? (
              documents.documentList?.map((document) => (
                <Table.Row key={document.id}>
                  <Table.Cell textAlign="center">
                    <input
                      type="checkbox"
                      onChange={handleSelectedBulkMail}
                      checked={!!selectedBulkMail[document.id]}
                      name={document.id}
                      disabled={!document.hasGraphicFlag}
                    />
                  </Table.Cell>
                  <Table.Cell textAlign="left">
                    <Link
                      to={`${match}/InvoiceDetail/${document.erpDocumentId}/${document.id}/0`}
                    >
                      <Popup
                        basic
                        content="Detalle de factura"
                        trigger={<Icon color="black" name="search plus" />}
                      />
                    </Link>
                    {document.hasGraphicFlag ? (
                      <Popup
                        content="Reenviar mail"
                        trigger={
                          <Button
                            icon
                            onClick={() =>
                              openModal(
                                <ResendMail document={document} />,
                                "Reenviar mail"
                              )
                            }
                          >
                            <Icon color="black" name="mail" />
                          </Button>
                        }
                      />
                    ) : (
                      <Popup
                        content="Documento sin representación gráfica. No es posible reenviar mail"
                        trigger={
                          <Button icon>
                            <Icon disabled color="black" name="mail" />
                          </Button>
                        }
                      />
                    )}

                    { document.hasGraphicFlag && (
                      <Link
                        to={`${match}/InvoiceDetail/${document.erpDocumentId}/${document.id}/1`}
                      >
                        <Popup
                          basic
                          content="Ver PDF"
                          trigger={<Icon color="black" name="file pdf" />}
                        />
                      </Link>
                    )} 
                  </Table.Cell>
                  <Table.Cell>{document.documentType}</Table.Cell>
                  <Table.Cell textAlign="left">
                    {document.erpDocumentId}
                  </Table.Cell>
                  <Table.Cell textAlign="left">{document.docId}</Table.Cell>
                  <Table.Cell textAlign="right">
                    {document.documentValue} {document.documentCurrencyCode}
                  </Table.Cell>
                  <Table.Cell>{document.issueDate}</Table.Cell>
                  <Table.Cell>{document.receptionDate}</Table.Cell>
                  <Table.Cell>{document.acquirerIdentification}</Table.Cell>
                  <Table.Cell>{document.acquirerName}</Table.Cell>
                  <Table.Cell>{document.validationDate}</Table.Cell>
                  <Table.Cell>{document.processStateMessage}</Table.Cell>
                  <Table.Cell>{document.emailStateAcquirer}</Table.Cell>
                </Table.Row>
              ))
            ) : (
              <Table.Row key="documentsNull">
                <Table.Cell>No se encontraron documentos</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
      <br />
      <div className="paginationTrackDocuments">
        <label>Registros por página</label>
        <Dropdown
          className="pageSize"
          name="pageSize"
          selection
          compact
          onChange={handlePageSizeChange}
          defaultValue={pageSize}
          options={pageSizeOptions}
        />
        <Pagination
          activePage={page}
          onPageChange={handlePaginationChange}
          siblingRange={2}
          totalPages={Math.trunc(documents.length / pageSize) + 1} //forumula para sacar el numero de paginas
        />
      </div>
    </div>
  );
};

export default observer(ListTrackDocuments);
